import { useNotification } from "hooks/useNotification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateSixMonthsInspectionDTO,
  SixMonthsInspection,
} from "types/sixMonthsInspection/sixMonthsInspection.type";
import instance from "./axiosInstance";
import { InspectionFilesDTO } from "./fiveYearsInspection.api";

export const SIX_MONTHS_INSPECTION_ENDPOINT = "/six-months-inspection";

export enum SixMonthsInspectionQueryKey {
  GetOne = "get-six-months-inspection",
}

export const useGetSixMonthsInspection = (id: number, shouldPoll = false) =>
  useQuery({
    queryKey: [SixMonthsInspectionQueryKey.GetOne, id],
    queryFn: async (): Promise<SixMonthsInspection> => {
      const response = await instance.get(
        `${SIX_MONTHS_INSPECTION_ENDPOINT}/${id}`,
      );
      return response.data;
    },
    refetchInterval: shouldPoll ? 3000 : false,
  });

export const useCreateSixMonthsInspection = () => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: CreateSixMonthsInspectionDTO,
    ): Promise<SixMonthsInspection> => {
      const response = await instance.post(
        SIX_MONTHS_INSPECTION_ENDPOINT,
        payload,
      );
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const useUpdateSixMonthsInspection = (options = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: any): Promise<SixMonthsInspection> => {
      const { emails, separator, ...rest } = payload;
      const response = await instance.put(SIX_MONTHS_INSPECTION_ENDPOINT, rest);
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (result: SixMonthsInspection) => {
      notification.info("Ändringarna sparade");
      queryClient.invalidateQueries({
        queryKey: [SixMonthsInspectionQueryKey.GetOne, result.id],
      });
    },
    ...options,
  });
};

export const useSetSixMonthsInspectionFiles = (options = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: InspectionFilesDTO,
    ): Promise<SixMonthsInspection> => {
      const response = await instance.put(
        SIX_MONTHS_INSPECTION_ENDPOINT + "/files",
        payload,
      );
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (result: SixMonthsInspection) => {
      queryClient.invalidateQueries({
        queryKey: [SixMonthsInspectionQueryKey.GetOne, result.id],
      });
    },
    ...options,
  });
};

export const useSendSixMonthsInspectionAttachments = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      id: number;
      includeTransportDocument: boolean;
    }): Promise<number> => {
      const response = await instance.post(
        SIX_MONTHS_INSPECTION_ENDPOINT + "/send-attachments",
        payload,
      );
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (id: number) => {
      notification.info("Utskick schemalagt");
      queryClient.invalidateQueries({
        queryKey: [SixMonthsInspectionQueryKey.GetOne, id],
      });
    },
  });
};

export const deleteSixMonthsInspection = () => ({
  mutationFn: async (id: number): Promise<void> =>
    (await instance.delete(`${SIX_MONTHS_INSPECTION_ENDPOINT}/${id}`)).data,
});
